import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, ButtonBase, Chip, CircularProgress, Container, Stack, Typography } from '@mui/material';
import { Add, ArrowForward, CheckCircle, ErrorOutline, Schedule, TaskAlt } from '@mui/icons-material';
import realtorService from '../../services/realtorService';
import { ProCard, fmtChecked } from '../directory/DirectoryLayout';
import { brand, text } from '../../theme/colors';

const INK = '#1e293b';
const TEAL_DEEP = '#134e4a';
const AMBER = '#9a3412';
// Tiles need to read as separate objects against both the white header and the
// pale page (owner, 2026-09-28: "each box should have a clearly highlighted border").
const EDGE = '#b9cbc7';
const TILE_SHADOW = '0 1px 2px rgba(19, 78, 74, 0.08)';
const EAC_LABEL = { approved: 'Registered with EAC', pending: 'EAC check pending', missing: 'Agency not linked', rejected: 'EAC standing lapsed' };
const LEVEL = {
  urgent: { icon: <ErrorOutline sx={{ color: AMBER }} />, bg: '#fdf2e9', edge: '#f0b48a' },
  soon: { icon: <Schedule sx={{ color: '#b45309' }} />, bg: '#fffbeb', edge: '#f3d38a' },
  todo: { icon: <TaskAlt sx={{ color: TEAL_DEEP }} />, bg: '#fff', edge: EDGE },
};

// Each number opens the listings page filtered to what it counts.
const STAT_TILES = [
  { key: 'live', label: 'Live listings', show: 'live' },
  { key: 'inProgress', label: 'In progress', show: 'in_progress' },
  { key: 'enquiries30', label: 'Enquiries, 30 days', show: 'enquiries' },
  { key: 'viewings30', label: 'Viewing requests, 30 days', show: 'viewings' },
  { key: 'saves', label: 'Saved by buyers', show: 'saved' },
];

const Panel = ({ title, action, children }) => (
  <Box component="section" sx={{ bgcolor: '#fff', borderRadius: 2, border: `1px solid ${EDGE}`, boxShadow: TILE_SHADOW, p: { xs: 2.25, md: 3 } }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
      <Typography component="h2" sx={{ fontSize: 20, fontWeight: 800, color: INK }}>{title}</Typography>
      {action}
    </Stack>
    {children}
  </Box>
);

/**
 * The realtor's home (owner, 2026-09-26: "the realtor role has not been updated
 * with all these changes"): what needs doing now — buyers waiting, mandates
 * running out, listings hidden or sent back — then the numbers, then how buyers
 * see them in Find a realtor. Before approval it is the application checklist.
 */
const RealtorDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    realtorService.dashboard()
      .then((res) => { if (res.success) setData(res.data); })
      .catch(() => setError('Failed to load your dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Container sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: brand.primary }} /></Container>;

  const approved = data?.approvalStatus === 'approved';
  const checklist = data?.documentChecklist;
  const missing = (checklist?.items || []).filter((i) => !i.optional && i.status !== 'approved');
  const attention = data?.attention;
  const dir = data?.directory;
  const stats = attention?.stats;

  return (
    <Box sx={{ bgcolor: '#f6f9f8', minHeight: '100vh' }}>
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #dfe6e4' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-end' }} justifyContent="space-between">
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: brand.primaryDark }}>
                Realtor · {EAC_LABEL[data?.eac?.state] || 'EAC check pending'}
              </Typography>
              <Typography component="h1" sx={{ fontSize: { xs: 28, md: 36 }, fontWeight: 800, color: INK, lineHeight: 1.15 }}>
                {data?.profile?.display_name ? `Welcome, ${data.profile.display_name}` : 'Your realtor dashboard'}
              </Typography>
            </Box>
            {approved && (
              <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/realtor/listings?new=1')}
                sx={{ height: 48, px: 3, borderRadius: '10px', bgcolor: brand.primaryDark, textTransform: 'none', fontWeight: 700, boxShadow: 'none', '&:hover': { bgcolor: TEAL_DEEP } }}>
                List a property
              </Button>
            )}
          </Stack>
          {approved && stats && (
            <Box sx={{ mt: 3, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 1.5 }}>
              {STAT_TILES.map((t) => (
                <ButtonBase key={t.key} onClick={() => navigate(`/realtor/listings?show=${t.show}`)} aria-label={`${stats[t.key]} ${t.label}: show these listings`}
                  sx={{
                    display: 'block', textAlign: 'left', p: 1.75, borderRadius: '10px', bgcolor: '#fff', border: `1px solid ${EDGE}`, boxShadow: TILE_SHADOW,
                    transition: 'border-color 120ms, background-color 120ms',
                    '&:hover, &.Mui-focusVisible': { borderColor: brand.primaryDark, bgcolor: '#f0f8f6' },
                    '&:hover .tile-arrow, &.Mui-focusVisible .tile-arrow': { opacity: 1, transform: 'translateX(0)' },
                  }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography sx={{ fontSize: 26, fontWeight: 800, color: INK, fontVariantNumeric: 'tabular-nums' }}>{stats[t.key]}</Typography>
                    <ArrowForward className="tile-arrow" sx={{ fontSize: 18, color: brand.primaryDark, opacity: 0.45, transform: 'translateX(-3px)', transition: 'opacity 120ms, transform 120ms' }} />
                  </Stack>
                  <Typography sx={{ fontSize: 13, color: text.secondary }}>{t.label}</Typography>
                </ButtonBase>
              ))}
            </Box>
          )}
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 380px' }, gap: 3.5, alignItems: 'start' }}>
          <Stack spacing={3}>
            {!approved && (
              <Panel title="Your application">
                <Typography sx={{ color: text.secondary, mb: 2 }}>Listing opens once Eqaya approves your account. This is what is still needed:</Typography>
                <Stack spacing={1}>
                  {(checklist?.items || []).filter((i) => !i.optional).map((i) => (
                    <Stack key={i.key} direction="row" spacing={1.25} alignItems="center">
                      {i.status === 'approved' ? <CheckCircle sx={{ color: TEAL_DEEP }} /> : <ErrorOutline sx={{ color: AMBER }} />}
                      <Typography sx={{ color: INK }}>{i.label}</Typography>
                    </Stack>
                  ))}
                </Stack>
                {data?.approvalNotes && <Alert severity="warning" sx={{ mt: 2 }}>{data.approvalNotes}</Alert>}
                <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
                  <Button onClick={() => navigate('/realtor/profile')} sx={{ textTransform: 'none', fontWeight: 700 }}>Profile and photo →</Button>
                  <Button onClick={() => navigate('/realtor/documents')} sx={{ textTransform: 'none', fontWeight: 700 }}>Documents →</Button>
                </Stack>
                {missing.length === 0 && <Typography sx={{ mt: 1, color: text.secondary }}>Everything is in. Eqaya is reviewing your application.</Typography>}
              </Panel>
            )}

            {approved && (
              <Panel title="Needs your attention" action={<Button onClick={() => navigate('/realtor/listings')} endIcon={<ArrowForward />} sx={{ textTransform: 'none', fontWeight: 700 }}>All listings</Button>}>
                {(attention?.items || []).length === 0 ? (
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ p: 2, borderRadius: '10px', bgcolor: '#fff', border: `1px solid ${EDGE}` }}>
                    <CheckCircle sx={{ color: TEAL_DEEP }} />
                    <Typography sx={{ color: INK }}>Nothing waiting on you. List a property, or make your directory card stronger.</Typography>
                  </Stack>
                ) : (
                  <Stack spacing={1}>
                    {attention.items.map((i, n) => (
                      <Stack key={`${i.kind}-${i.listingId}-${n}`} direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 1.5 }} alignItems={{ sm: 'center' }}
                        sx={{ p: 1.75, borderRadius: '10px', bgcolor: LEVEL[i.level]?.bg || '#fff', border: `1px solid ${LEVEL[i.level]?.edge || EDGE}` }}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
                          {LEVEL[i.level]?.icon}
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700, color: INK }}>{i.title}</Typography>
                            <Typography sx={{ fontSize: 14.5, color: text.secondary }}>{i.text}</Typography>
                          </Box>
                        </Stack>
                        <Button onClick={() => navigate(i.action.to)} variant={i.level === 'urgent' ? 'contained' : 'outlined'}
                          sx={{ flexShrink: 0, borderRadius: '10px', textTransform: 'none', fontWeight: 700, boxShadow: 'none', ...(i.level === 'urgent' ? { bgcolor: brand.primaryDark, '&:hover': { bgcolor: TEAL_DEEP } } : {}) }}>
                          {i.action.label}
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Panel>
            )}
          </Stack>

          <Stack spacing={2.25}>
            <Panel title="How buyers see you">
              {dir?.card ? (
                <>
                  <Chip size="small" label={dir.listed ? 'Showing in Find a realtor' : 'Not showing in Find a realtor yet'}
                    sx={{ mb: 1.5, fontWeight: 700, borderRadius: '6px', bgcolor: dir.listed ? '#e6f4f2' : '#fdf2e9', color: dir.listed ? TEAL_DEEP : AMBER }} />
                  <ProCard
                    name={dir.card.displayName}
                    firm={dir.card.agency}
                    photo={dir.card.photoUrl}
                    badge={`In good standing with the EAC · ${fmtChecked(dir.card.standing?.checkedAt)}`}
                    chips={[
                      ...((dir.card.sells || []).length === 2 ? ['Houses and stands'] : (dir.card.sells || []).map((s) => (s === 'house' ? 'Houses' : 'Stands'))),
                      ...(dir.card.yearsExperience ? [`${dir.card.yearsExperience} years in the trade`] : []),
                    ]}
                    details={[dir.card.listingCount ? `**${dir.card.listingCount} verified home${dir.card.listingCount === 1 ? '' : 's'} for sale` : 'No homes listed yet', (dir.card.towns || []).join(' · ') || null]}
                  />
                  {(dir.reasons || []).length > 0 && (
                    <Stack spacing={0.75} sx={{ mt: 2 }}>
                      {dir.reasons.map((r) => <Typography key={r} sx={{ fontSize: 14, color: AMBER, fontWeight: 600 }}>• {r}</Typography>)}
                    </Stack>
                  )}
                  {(dir.tips || []).length > 0 && (
                    <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                      {dir.tips.map((t) => <Typography key={t} sx={{ fontSize: 14, color: text.secondary }}>• {t}</Typography>)}
                    </Stack>
                  )}
                  <Button onClick={() => navigate('/realtor/profile')} endIcon={<ArrowForward />} sx={{ mt: 1.5, textTransform: 'none', fontWeight: 700 }}>Edit profile and photo</Button>
                </>
              ) : (
                <Button onClick={() => navigate('/realtor/profile')} endIcon={<ArrowForward />} sx={{ textTransform: 'none', fontWeight: 700 }}>Set up your profile</Button>
              )}
            </Panel>
            <Panel title="Your paperwork">
              <Typography sx={{ fontSize: 14.5, color: text.secondary, mb: 1 }}>
                {missing.length ? `Still needed: ${missing.map((m) => m.label).join(', ')}.` : 'ID, EAC practising certificate and photo are in.'}
              </Typography>
              <Button onClick={() => navigate('/realtor/documents')} endIcon={<ArrowForward />} sx={{ textTransform: 'none', fontWeight: 700 }}>Documents</Button>
            </Panel>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default RealtorDashboard;
